import React, { useState, useEffect, useCallback } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { FiRefreshCw, FiSend, FiSmartphone, FiX } from 'react-icons/fi';
import deviceService from '../services/deviceService';
import storeService from '../services/storeService';

// Remote command types the backend accepts (DeviceCommand.TYPES). `wipe` is
// destructive, so it gets an extra confirmation before it is enqueued.
const COMMAND_TYPES = [
  { value: 'relaunch_pos', label: 'Relaunch POS' },
  { value: 'clear_cache', label: 'Clear cache' },
  { value: 'reboot', label: 'Reboot device' },
  { value: 'locate', label: 'Locate (ring/flash)' },
  { value: 'push_config', label: 'Push config' },
  { value: 'wipe', label: 'Wipe device', destructive: true },
];

const STATUS_STYLES = {
  active: 'bg-green-100 text-green-800',
  retired: 'bg-gray-200 text-gray-700',
  lost: 'bg-red-100 text-red-800',
};
const COMMAND_STATUS_STYLES = {
  pending: 'bg-yellow-100 text-yellow-800',
  delivered: 'bg-blue-100 text-blue-800',
  acknowledged: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
};

// A device is considered stale (agent not heartbeating) if unseen for > 1h.
const STALE_MS = 60 * 60 * 1000;

const relativeSeen = (lastSeen) => {
  if (!lastSeen) return 'never';
  try {
    return `${formatDistanceToNow(new Date(lastSeen))} ago`;
  } catch (_) {
    return '—';
  }
};

const DeviceFleetPage = () => {
  const [devices, setDevices] = useState([]);
  const [stores, setStores] = useState([]);
  // Store reassignment needs stores:read, which a devices:manage admin may not
  // have. Track whether the store list actually loaded so we can offer the
  // dropdown only when it did, instead of silently showing an empty one.
  const [canAssignStores, setCanAssignStores] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // deviceId being managed

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    try {
      const deviceList = await deviceService.getDevices();
      setDevices(deviceList);
      // Fetch stores separately so a stores:read 403 doesn't block the fleet
      // view — it just disables reassignment with an explanation.
      try {
        const storeList = await storeService.getStores();
        setStores(storeList || []);
        setCanAssignStores(true);
      } catch (_) {
        setStores([]);
        setCanAssignStores(false);
      }
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load devices.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const handleAssignStore = async (deviceId, storeId) => {
    try {
      await deviceService.updateDevice(deviceId, { store: storeId || null });
      fetchDevices();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update device.');
    }
  };

  const selectedDevice = devices.find((d) => d.deviceId === selected) || null;

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center">
          <FiSmartphone className="mr-3" /> Device Fleet
        </h1>
        <button
          onClick={fetchDevices}
          className="flex items-center bg-white border px-4 py-2 rounded-md shadow-sm hover:bg-gray-50"
        >
          <FiRefreshCw className="mr-2" /> Refresh
        </button>
      </div>

      {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}

      <div className="bg-white shadow-md rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              <th className="px-4 py-3">Device</th>
              <th className="px-4 py-3">Target</th>
              <th className="px-4 py-3">Store</th>
              <th className="px-4 py-3">Native / Web</th>
              <th className="px-4 py-3">Kiosk</th>
              <th className="px-4 py-3">Last seen</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {!loading && devices.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                  No devices enrolled yet.
                </td>
              </tr>
            )}
            {devices.map((device) => {
              const stale = device.lastSeen && Date.now() - new Date(device.lastSeen).getTime() > STALE_MS;
              return (
                <tr key={device.deviceId} className="text-sm">
                  <td className="px-4 py-4">
                    <div className="font-semibold text-gray-800">{device.model || 'Unknown model'}</div>
                    <div className="font-mono text-xs text-gray-500">{device.deviceId}</div>
                  </td>
                  <td className="px-4 py-4 uppercase text-xs font-semibold text-gray-600">
                    {device.target}
                  </td>
                  <td className="px-4 py-4">
                    {canAssignStores ? (
                      <select
                        value={device.store?._id || device.store || ''}
                        onChange={(e) => handleAssignStore(device.deviceId, e.target.value)}
                        className="border rounded-md p-1 text-sm bg-white"
                      >
                        <option value="">— Unassigned —</option>
                        {stores.map((s) => (
                          <option key={s._id} value={s._id}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span
                        className="text-gray-600"
                        title="Reassignment needs the stores:read permission"
                      >
                        {device.store?.name || '— Unassigned —'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <div>{device.nativeVersion || '—'}{device.nativeVersionCode ? ` (${device.nativeVersionCode})` : ''}</div>
                    <div className="text-xs text-gray-500">web {device.webVersion || '—'}</div>
                  </td>
                  <td className="px-4 py-4 capitalize">{device.kioskState || 'unknown'}</td>
                  <td className={`px-4 py-4 ${stale ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                    {relativeSeen(device.lastSeen)}
                  </td>
                  <td className="px-4 py-4">
                    <span
                      className={`px-2 inline-flex text-xs font-semibold rounded-full ${
                        STATUS_STYLES[device.status] || 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {device.status}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <button
                      onClick={() => setSelected(device.deviceId)}
                      className="text-primary hover:underline font-medium"
                    >
                      Manage
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selectedDevice && (
        <DeviceDrawer
          device={selectedDevice}
          onClose={() => setSelected(null)}
          onError={setError}
        />
      )}
    </div>
  );
};

// Slide-over drawer: full device detail + issue-command form + command history.
const DeviceDrawer = ({ device, onClose, onError }) => {
  const [commandType, setCommandType] = useState('relaunch_pos');
  const [payload, setPayload] = useState('');
  const [commands, setCommands] = useState([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');

  const loadCommands = useCallback(async () => {
    try {
      setCommands(await deviceService.getCommands(device.deviceId));
    } catch (err) {
      onError(err.response?.data?.message || 'Failed to load command history.');
    }
  }, [device.deviceId, onError]);

  useEffect(() => {
    loadCommands();
  }, [loadCommands]);

  const handleIssue = async (e) => {
    e.preventDefault();
    const chosen = COMMAND_TYPES.find((c) => c.value === commandType);
    if (chosen?.destructive) {
      const ok = window.confirm(
        `Wipe ${device.model || device.deviceId}? This factory-resets the device and cannot be undone.`
      );
      if (!ok) return;
    }
    // Payload only applies to push_config; never send a stale payload left over
    // from a previous selection for a command type that has no payload field.
    let parsedPayload = null;
    if (commandType === 'push_config' && payload.trim()) {
      try {
        parsedPayload = JSON.parse(payload);
      } catch (_) {
        onError('Payload must be valid JSON.');
        return;
      }
    }
    setBusy(true);
    try {
      await deviceService.issueCommand(device.deviceId, commandType, parsedPayload);
      setPayload('');
      setNotice(`Queued "${commandType}". The device will pick it up on its next heartbeat.`);
      loadCommands();
    } catch (err) {
      onError(err.response?.data?.message || 'Failed to issue command.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={onClose} />
      <div className="relative bg-white w-full max-w-md h-full shadow-xl overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white">
          <div>
            <h2 className="text-xl font-bold">{device.model || 'Device'}</h2>
            <p className="font-mono text-xs text-gray-500">{device.deviceId}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800">
            <FiX size={22} />
          </button>
        </div>

        <div className="p-4 space-y-6">
          <section>
            <h3 className="font-semibold text-gray-700 mb-2">Details</h3>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <dt className="text-gray-500">Serial</dt>
              <dd>{device.serial || '—'}</dd>
              <dt className="text-gray-500">OS</dt>
              <dd>{device.os || '—'} {device.osVersion || ''} {device.sdkInt ? `(SDK ${device.sdkInt})` : ''}</dd>
              <dt className="text-gray-500">Native version</dt>
              <dd>{device.nativeVersion || '—'}{device.nativeVersionCode ? ` (${device.nativeVersionCode})` : ''}</dd>
              <dt className="text-gray-500">Web version</dt>
              <dd>{device.webVersion || '—'}</dd>
              <dt className="text-gray-500">Kiosk</dt>
              <dd className="capitalize">{device.kioskState || 'unknown'}</dd>
              <dt className="text-gray-500">Battery</dt>
              <dd>{device.batteryLevel != null ? `${device.batteryLevel}%` : '—'}</dd>
              <dt className="text-gray-500">Enrolled</dt>
              <dd>{device.enrolledAt ? format(new Date(device.enrolledAt), 'dd-MM-yyyy') : '—'}</dd>
              <dt className="text-gray-500">Last seen</dt>
              <dd>{relativeSeen(device.lastSeen)}</dd>
            </dl>
          </section>

          <section>
            <h3 className="font-semibold text-gray-700 mb-2">Send command</h3>
            {notice && <p className="bg-blue-50 text-blue-700 text-sm p-2 rounded mb-2">{notice}</p>}
            <form onSubmit={handleIssue} className="space-y-2">
              <select
                value={commandType}
                onChange={(e) => {
                  setCommandType(e.target.value);
                  setPayload(''); // drop any payload from the previous selection
                }}
                className="border rounded-md p-2 w-full text-sm"
              >
                {COMMAND_TYPES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
              {commandType === 'push_config' && (
                <textarea
                  value={payload}
                  onChange={(e) => setPayload(e.target.value)}
                  placeholder='Optional JSON payload, e.g. {"key":"value"}'
                  rows={3}
                  className="border rounded-md p-2 w-full font-mono text-xs"
                />
              )}
              <button
                type="submit"
                disabled={busy}
                className="flex items-center justify-center w-full bg-primary text-white py-2 rounded-md disabled:opacity-50"
              >
                <FiSend className="mr-2" /> {busy ? 'Queuing…' : 'Queue command'}
              </button>
            </form>
          </section>

          <section>
            <h3 className="font-semibold text-gray-700 mb-2">Command history</h3>
            {commands.length === 0 ? (
              <p className="text-sm text-gray-500">No commands issued yet.</p>
            ) : (
              <ul className="space-y-2">
                {commands.map((cmd) => (
                  <li key={cmd._id} className="border rounded-md p-2 text-sm flex justify-between items-center">
                    <div>
                      <div className="font-medium">{cmd.type}</div>
                      <div className="text-xs text-gray-500">
                        {cmd.createdAt ? format(new Date(cmd.createdAt), 'dd-MM-yyyy HH:mm') : ''}
                        {cmd.createdBy?.name ? ` · ${cmd.createdBy.name}` : ''}
                      </div>
                    </div>
                    <span
                      className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                        COMMAND_STATUS_STYLES[cmd.status] || 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {cmd.status}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default DeviceFleetPage;
